import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchEvents,
  upsertEvent as apiUpsertEvent,
  deleteEvent as apiDeleteEvent,
  fetchLockState,
  setLockState as apiSetLockState,
  SCHEDULER_KEYS,
} from "../api/schedulerApi";
import type {
  SchedulerEvent as ApiEvent,
  SchedulerEventInput,
  LockState,
} from "../api/schedulerApi";

/* ── Re-export the API event type for grid/editor usage ──────────── */
export type SchedulerEvent = ApiEvent;

/* ── Types ─────────────────────────────────────────────────────────── */

export interface SchedulerState {
  weekStart: Date;
  dragStart: { date: string; slot: number } | null;
  dragEnd: { date: string; slot: number } | null;
  editingEvent: SchedulerEvent | null;
}

type Action =
  | { type: "SET_WEEK"; weekStart: Date }
  | { type: "NAV_PREV_WEEK" }
  | { type: "NAV_NEXT_WEEK" }
  | { type: "NAV_TODAY" }
  | { type: "DRAG_START"; date: string; slot: number }
  | { type: "DRAG_MOVE"; date: string; slot: number }
  | { type: "DRAG_END"; eventCount: number }
  | { type: "DRAG_CANCEL" }
  | { type: "SET_EDITING"; event: SchedulerEvent | null }
  | { type: "CLEAR_EDITING" };

/* ── Helpers ───────────────────────────────────────────────────────── */

const EVENT_COLORS = [
  "#a78bfa", "#fb923c", "#4ade80", "#22d3ee",
  "#f87171", "#facc15", "#e879f9", "#38bdf8",
] as const;

export function getMonday(d: Date): Date {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy;
}

export function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

export function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Convert a 15-min slot index (0-95) to HH:MM string. */
export function slotToTime(slot: number): string {
  const h = Math.floor(slot / 4);
  const m = (slot % 4) * 15;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Convert HH:MM string to 15-min slot index. */
export function timeToSlot(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 4 + Math.floor(m / 15);
}

/** Generate all 15-min time options for a dropdown. */
export function generateTimeOptions(): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  for (let slot = 0; slot < 96; slot++) {
    const time = slotToTime(slot);
    opts.push({ value: time, label: time });
  }
  return opts;
}

export function getWeekDays(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export function formatDayHeader(d: Date): string {
  const dow = (d.getDay() + 6) % 7;
  return `${DAY_LABELS[dow]} ${d.getDate()}/${d.getMonth() + 1}`;
}

/* ── Reducer (UI state only — no event/lock storage) ──────────────── */

function reducer(state: SchedulerState, action: Action): SchedulerState {
  switch (action.type) {
    case "SET_WEEK":
      return { ...state, weekStart: getMonday(action.weekStart) };

    case "NAV_PREV_WEEK":
      return { ...state, weekStart: addDays(state.weekStart, -7) };

    case "NAV_NEXT_WEEK":
      return { ...state, weekStart: addDays(state.weekStart, 7) };

    case "NAV_TODAY":
      return { ...state, weekStart: getMonday(new Date()) };

    case "DRAG_START":
      return {
        ...state,
        dragStart: { date: action.date, slot: action.slot },
        dragEnd: { date: action.date, slot: action.slot },
      };

    case "DRAG_MOVE":
      if (!state.dragStart) return state;
      return {
        ...state,
        dragEnd: { date: state.dragStart.date, slot: action.slot },
      };

    case "DRAG_END": {
      if (!state.dragStart || !state.dragEnd)
        return { ...state, dragStart: null, dragEnd: null };
      const s = Math.min(state.dragStart.slot, state.dragEnd.slot);
      const e = Math.max(state.dragStart.slot, state.dragEnd.slot) + 1;
      const color = EVENT_COLORS[action.eventCount % EVENT_COLORS.length];
      const draft: SchedulerEvent = {
        id: "",
        title: "",
        date: state.dragStart.date,
        start_time: slotToTime(s),
        end_time: slotToTime(e),
        start_slot: s,
        end_slot: e,
        color,
        notes: "",
        all_day: false,
        recurring: "none",
        active: true,
        created_by: null,
        created_at: null,
        updated_at: null,
      };
      return {
        ...state,
        dragStart: null,
        dragEnd: null,
        editingEvent: draft,
      };
    }

    case "DRAG_CANCEL":
      return { ...state, dragStart: null, dragEnd: null };

    case "SET_EDITING":
      return { ...state, editingEvent: action.event };

    case "CLEAR_EDITING":
      return { ...state, editingEvent: null };

    default:
      return state;
  }
}

/* ── Hook ──────────────────────────────────────────────────────────── */

function initialState(): SchedulerState {
  return {
    weekStart: getMonday(new Date()),
    dragStart: null,
    dragEnd: null,
    editingEvent: null,
  };
}

export function useScheduler() {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const queryClient = useQueryClient();

  const weekDays = useMemo(() => getWeekDays(state.weekStart), [state.weekStart]);
  const weekEnd = useMemo(() => addDays(state.weekStart, 6), [state.weekStart]);

  const fromKey = formatDateKey(state.weekStart);
  const toKey = formatDateKey(weekEnd);

  // ── Fetch events for current week ──────────────────────────────
  const eventsQuery = useQuery({
    queryKey: SCHEDULER_KEYS.eventsByRange(fromKey, toKey),
    queryFn: () => fetchEvents(fromKey, toKey),
    staleTime: 30_000,
  });

  const events = eventsQuery.data?.items ?? [];

  // ── Fetch lock state ───────────────────────────────────────────
  const lockQuery = useQuery({
    queryKey: SCHEDULER_KEYS.lock(),
    queryFn: fetchLockState,
    staleTime: 60_000,
  });

  const locked = lockQuery.data?.locked ?? false;

  // ── Mutations ──────────────────────────────────────────────────
  const upsertMutation = useMutation({
    mutationFn: (input: SchedulerEventInput) => apiUpsertEvent(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.events() });
      dispatch({ type: "CLEAR_EDITING" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDeleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.events() });
      dispatch({ type: "CLEAR_EDITING" });
    },
  });

  const lockMutation = useMutation({
    mutationFn: ({ locked, reason }: { locked: boolean; reason?: string }) =>
      apiSetLockState(locked, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.lock() });
    },
  });

  // ── Callbacks ──────────────────────────────────────────────────

  const setWeek = useCallback(
    (d: Date) => dispatch({ type: "SET_WEEK", weekStart: d }),
    [],
  );
  const navPrevWeek = useCallback(() => dispatch({ type: "NAV_PREV_WEEK" }), []);
  const navNextWeek = useCallback(() => dispatch({ type: "NAV_NEXT_WEEK" }), []);
  const navToday = useCallback(() => dispatch({ type: "NAV_TODAY" }), []);

  const toggleLock = useCallback(() => {
    lockMutation.mutate({ locked: !locked });
  }, [locked, lockMutation]);

  const dragStart = useCallback(
    (date: string, slot: number) => {
      if (locked) return;
      dispatch({ type: "DRAG_START", date, slot });
    },
    [locked],
  );
  const dragMove = useCallback(
    (date: string, slot: number) => dispatch({ type: "DRAG_MOVE", date, slot }),
    [],
  );
  const dragEnd = useCallback(
    () => dispatch({ type: "DRAG_END", eventCount: events.length }),
    [events.length],
  );
  const dragCancel = useCallback(() => dispatch({ type: "DRAG_CANCEL" }), []);

  const saveEvent = useCallback(
    (event: SchedulerEvent) => {
      const input: SchedulerEventInput = {
        id: event.id || undefined,
        title: event.title,
        date: event.date,
        start_time: event.start_time,
        end_time: event.end_time,
        start_slot: event.start_slot,
        end_slot: event.end_slot,
        color: event.color,
        notes: event.notes,
        recurring: event.recurring,
      };
      upsertMutation.mutate(input);
    },
    [upsertMutation],
  );

  const removeEvent = useCallback(
    (id: string) => deleteMutation.mutate(id),
    [deleteMutation],
  );

  const setEditing = useCallback(
    (event: SchedulerEvent | null) =>
      event
        ? dispatch({ type: "SET_EDITING", event })
        : dispatch({ type: "CLEAR_EDITING" }),
    [],
  );

  const eventsForDay = useCallback(
    (dateKey: string) => events.filter((ev) => ev.date === dateKey),
    [events],
  );

  const dragRange = useMemo(() => {
    if (!state.dragStart || !state.dragEnd) return null;
    return {
      date: state.dragStart.date,
      startSlot: Math.min(state.dragStart.slot, state.dragEnd.slot),
      endSlot: Math.max(state.dragStart.slot, state.dragEnd.slot),
    };
  }, [state.dragStart, state.dragEnd]);

  return {
    weekStart: state.weekStart,
    editingEvent: state.editingEvent,
    events,
    locked,
    loading: eventsQuery.isLoading || lockQuery.isLoading,
    saving: upsertMutation.isPending || deleteMutation.isPending,
    weekDays,
    dragRange,
    setWeek,
    navPrevWeek,
    navNextWeek,
    navToday,
    toggleLock,
    dragStart,
    dragMove,
    dragEnd,
    dragCancel,
    saveEvent,
    removeEvent,
    setEditing,
    eventsForDay,
  } as const;
}
